import { IDBPObjectStore } from 'idb';
import { merge } from 'lodash-es';
import pLimit from 'p-limit';
import { OPERATION } from '../../des.dto';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback, ProgressStats } from '../executors';
import { AbstractWarehouseSyncStepWorker } from './abstract-warehouse-worker.utils';
import { IdbIssueMedia } from '../../issue/issue.dto';
import { Dto, HttpUtils, logger } from '@damofire/data-commons';

export abstract class WarehouseFilesStepsWorker<T extends Dto<string>> extends AbstractWarehouseSyncStepWorker {
  protected maxDownloadChunkSize = 10;
  protected maxDownloadConcurrency = 5;
  protected cfBaseUrl: string;

  protected constructor(
    protected readonly store: string,
    protected readonly downloadPath: string,
    protected readonly display: string,
  ) {
    super(store, display);
  }

  override async execute(
    environment: any,
    ctx: WarehouseSyncStepTaskContext,
    onProgress: (progress: number) => void,
    itemsProcessedCallback: (change: { data: Dto<string>; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    this.maxDownloadChunkSize = environment.appConfig.watson.maxDownloadChunkSize || 10;
    // Safari caps connections per host (~6) and reports excess cross-origin
    // XHRs killed by the cap as CORS ("access control") errors — stay below it.
    this.maxDownloadConcurrency = environment.appConfig.watson.maxDownloadConcurrency || 5;
    this.cfBaseUrl = environment.appConfig.fns;

    return super.execute(environment, ctx, onProgress, itemsProcessedCallback);
  }

  protected abstract stripMedia(im: T): T;

  protected abstract persist(item: IdbIssueMedia, objectStore: IDBPObjectStore<unknown, [string], string, IDBTransactionMode>): T;

  protected abstract filterChanges(allFromStore: T[]): string[];

  /** Extra query params sent to the download endpoint; subclasses opt into server-side behaviours (e.g. AVIF opt-in). */
  protected downloadParams(_ctx: WarehouseSyncStepTaskContext): Record<string, unknown> {
    return {};
  }

  protected abstract sendToMainThread(im: IdbIssueMedia, itemsProcessedCallback: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void): void;

  protected async download(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: ProgressCallback,
    itemsProcessedCallback?: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    logger.debug('Starting image download worker', { division: ctx.division, store: this.store, category: 'sync' });
    const changes = await this.syncDivisionMedias(ctx, onProgress, itemsProcessedCallback);

    logger.debug('Finishing image download worker', { division: ctx.division, store: this.store, total: changes.length, category: 'sync' });
  }

  protected async filterMedias(allFromDivision: T[], _ctx: WarehouseSyncStepTaskContext): Promise<T[]> {
    return allFromDivision;
  }

  private async syncDivisionMedias(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: ProgressCallback,
    itemsProcessedCallback?: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<T[]> {
    const allFromDivision: T[] = await this.idbService.getAllFromIndex(this.store, 'division', ctx.division);
    const filteredMedias: T[] = await this.filterMedias(allFromDivision, ctx);

    if (!filteredMedias.length) {
      logger.debug('Filter produced 0 medias, skipping download', { store: this.store, category: 'sync' });
      return [];
    }

    // Only what is missing on the device is requested: no chunk is wasted on filtered-out or already-downloaded medias.
    const onDevice = new Set(this.filterChanges(filteredMedias));
    const pending = filteredMedias.filter((m) => !onDevice.has(this.filenameOf(m)));
    const chunks = this.toChunks(pending);
    const toDownloadCnt = filteredMedias.length;
    let downloaded = toDownloadCnt - pending.length;

    logger.info(`${this.store} download divided in ${chunks.length} chunks. [${pending.length} to download, ${downloaded} already on device, ${allFromDivision.length} total]`, {
      division: ctx.division,
      store: this.store,
      category: 'sync',
    });

    // Report immediately so listeners show RUNNING n/expected instead of silence until the first chunk returns.
    onProgress?.(Math.ceil((downloaded / toDownloadCnt) * 100), { processed: downloaded, expected: toDownloadCnt });

    const failedChunks: number[] = [];
    const limit = pLimit(this.maxDownloadConcurrency);
    const results = await Promise.all(
      chunks.map((chunk, chunkIndex) =>
        limit(() =>
          this.shouldStopTask
            ? Promise.resolve([] as T[])
            : this.downloadChunk(ctx, chunkIndex, chunk, (size: number) => (downloaded = this.updateProgress(downloaded, toDownloadCnt, size, onProgress)), itemsProcessedCallback).catch((err) =>
                this.onChunkFailure(failedChunks, chunkIndex, ctx, err),
              ),
        ),
      ),
    );

    this.throwIfChunksFailed(failedChunks, chunks.length, ctx);

    return results.flat();
  }

  private toChunks(medias: T[]): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < medias.length; i += this.maxDownloadChunkSize) {
      chunks.push(medias.slice(i, i + this.maxDownloadChunkSize));
    }
    return chunks;
  }

  private filenameOf(media: T): string {
    return (media as unknown as IdbIssueMedia).filename;
  }

  // `issueId/mediaId`: lets the server read the documents directly instead of scanning the whole warehouse for each page.
  private refOf(media: T): string {
    return `${(media as unknown as IdbIssueMedia).issue?._id}/${media._id}`;
  }

  private onChunkFailure(failedChunks: number[], chunkOffset: number, ctx: WarehouseSyncStepTaskContext, err: unknown): T[] {
    failedChunks.push(chunkOffset);
    logger.error('Download chunk failed, will report partial failure', { chunkIndex: chunkOffset, store: this.store, division: ctx.division, error: err, category: 'sync' });
    return [];
  }

  private throwIfChunksFailed(failedChunks: number[], totalChunksCount: number, ctx: WarehouseSyncStepTaskContext): void {
    if (failedChunks.length && !this.shouldStopTask) {
      throw new Error(`${this.store} download incomplete: ${failedChunks.length}/${totalChunksCount} chunks failed for division ${ctx.division} (chunks: ${failedChunks.join(',')})`);
    }
  }

  private updateProgress(totalItemsDownloaded: number, totalToDownload: number, size: number, onProgress?: ProgressCallback): number {
    const newTotal = totalItemsDownloaded + size;
    const newProgress = Math.ceil((newTotal / totalToDownload) * 100);
    logger.debug('Total items downloaded', { progress: newProgress, category: 'sync' });

    if (onProgress) {
      const stats: ProgressStats = { processed: newTotal, expected: totalToDownload };
      onProgress(newProgress, stats);
    }

    return newTotal;
  }

  private async downloadChunk(
    ctx: WarehouseSyncStepTaskContext,
    chunkIndex: number,
    chunkMedia: T[],
    onProgress: (size: number) => void,
    itemsProcessedCallback: (change: { data: T; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<T[]> {
    logger.debug('Downloading chunk', { chunkIndex, store: this.store, size: chunkMedia.length, category: 'sync' });
    const changes: T[] = [];

    try {
      const medias = await HttpUtils.get<{ items: T[]; byIds?: boolean }>(this.cfBaseUrl, `ww-warehouses/${ctx.division}/${this.downloadPath}`, {
        ids: chunkMedia.map((m) => this.refOf(m)),
        ...this.downloadParams(ctx),
      });

      if (!medias.byIds) {
        throw new Error('The server does not support downloading medias by id yet. Deploy the functions before this app version.');
      }

      if (medias.items?.length && !this.shouldStopTask) {
        const tx = await this.idbService.createTransaction(this.store);
        const objectStore = tx.objectStore(this.store);

        for (const i of medias.items) {
          const matching: IdbIssueMedia = chunkMedia.find((idbMedia) => idbMedia._id === i._id);
          if (!matching) {
            continue;
          }
          merge(matching, i, { syncId: ctx.syncId });

          const persisted: T = this.persist(matching, objectStore);
          changes.push(persisted);

          if (matching.bytes) {
            logger.debug('sendToMainThread', { bytesPath: matching.bytesPath, thumbBytesPath: matching.thumbBytesPath, id: matching._id, issueId: matching.issue?._id, category: 'sync' });
            this.sendToMainThread(matching, itemsProcessedCallback);
          }
        }

        await this.idbService.commitTransaction(tx, [], { store: this.store, division: ctx.division, chunkIndex });
      }

      onProgress(chunkMedia.length);
    } catch (err) {
      logger.error('Cannot download chunk', { chunkIndex, store: this.store, error: err, category: 'sync' });
      if (this.shouldStopTask) {
        return changes;
      }
      throw err;
    }

    return changes;
  }
}
